import React, { useState, useEffect } from "react";
import "./Queris.css";
import { useNavigate, useLocation } from "react-router-dom";

const ExpertQA = () => {
  const BASE_URL = "https://marketapi.etnownews.com";
  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const urlTab = queryParams.get("tab") || "public";
  const urlQuestionId = queryParams.get("questionId");

  const [isLoggedIn, setIsLoggedIn] = useState(
    !!localStorage.getItem("auth_token")
  );
  const [currentUserType, setCurrentUserType] = useState(
    localStorage.getItem("auth_token") ? "expert" : "user"
  );
  const [activeTab, setActiveTab] = useState(urlTab);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [typedAnalysis, setTypedAnalysis] = useState("");
  const [submitError, setSubmitError] = useState("");

  const [toastMessage, setToastMessage] = useState("");

  const [questions, setQuestions] = useState([]);
  const [myAnswers, setMyAnswers] = useState([]);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [isApiLoading, setIsApiLoading] = useState(false);
  const [isModalLoading, setIsModalLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(30);

  const [languageTab, setLanguageTab] = useState("english");

  const [quickStats, setQuickStats] = useState({
    question_count: 0,
    answer_count: 0,
  });
  const [expertName, setExpertName] = useState("Guest User");

  const showToast = (msg) => {
    setToastMessage(msg);
    setTimeout(() => {
      setToastMessage("");
    }, 3500);
  };

  useEffect(() => {
    const freshToken = localStorage.getItem("auth_token");
    const tokenExists = !!freshToken;

    setIsLoggedIn(tokenExists);
    setCurrentUserType(tokenExists ? "expert" : "user");

    const currentTab = queryParams.get("tab") || "public";
    if (!tokenExists && currentTab === "responses") {
      setActiveTab("public");
      navigate("/questions?tab=public", { replace: true });
    } else {
      setActiveTab(currentTab);
    }
  }, [location.search]);

  useEffect(() => {
    if (isLoggedIn) {
      const fName = localStorage.getItem("user_first_name") || "";
      const lName = localStorage.getItem("user_last_name") || "";
      if (fName) {
        setExpertName(`${fName} ${lName}`.trim());
      }
    } else {
      setExpertName("Guest");
    }
  }, [isLoggedIn]);

  useEffect(() => {
    const checkTokenExpiry = () => {
      const token = localStorage.getItem("auth_token");
      const expiry = localStorage.getItem("token_expiry");

      if (token && expiry) {
        if (Date.now() > parseInt(expiry, 10)) {
          localStorage.removeItem("auth_token");
          localStorage.removeItem("token_expiry");
          localStorage.removeItem("user_first_name");
          localStorage.removeItem("user_last_name");

          setMyAnswers([]);
          setIsLoggedIn(false);
          setCurrentUserType("user");
          navigate("/questions?tab=public", { replace: true });
        }
      }
    };

    checkTokenExpiry();
    const interval = setInterval(checkTokenExpiry, 60000);
    return () => clearInterval(interval);
  }, [navigate]);

  const fetchQuestions = async () => {
    setIsApiLoading(true);
    try {
      const source =
        languageTab === "hindi" ? "et_now_hindi" : "et_now_english";
      const currentToken = localStorage.getItem("auth_token");
      const headers = {};
      if (currentToken) {
        headers["Authorization"] = `Bearer ${currentToken}`;
      }

      const url = `${BASE_URL}/api/v1/account_questions?whatsapp_source=${source}&page=${currentPage}&per_page=${perPage}`;

      const res = await fetch(url, { headers });
      const data = await res.json();

      if (data?.data) {
        setQuestions(data.data);
      } else {
        setQuestions([]);
      }
    } catch (err) {
      console.error("Failed to fetch questions:", err);
      setQuestions([]);
    } finally {
      setIsApiLoading(false);
    }
  };

  const fetchMyAnswers = async () => {
    const currentToken = localStorage.getItem("auth_token");
    if (!currentToken) return;

    setIsApiLoading(true);
    try {
      const url = `${BASE_URL}/api/v1/account_question_answers?page=${currentPage}&per_page=${perPage}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${currentToken}` },
      });
      const data = await res.json();
      if (data?.data) {
        setMyAnswers(data.data);
      } else {
        setMyAnswers([]);
      }
    } catch (err) {
      console.error("Failed to fetch answers:", err);
    } finally {
      setIsApiLoading(false);
    }
  };

  const fetchQuickStats = async () => {
    const currentToken = localStorage.getItem("auth_token");
    try {
      const headers = {};
      if (currentToken) {
        headers["Authorization"] = `Bearer ${currentToken}`;
      }
      const res = await fetch(
        `${BASE_URL}/api/v1/account_questions/quick_stats`,
        { headers }
      );
      const data = await res.json();
      if (data) {
        setQuickStats({
          question_count: data.question_count || 0,
          answer_count: data.answer_count || 0,
        });
      }
    } catch (err) {
      console.error("Failed to fetch quick stats:", err);
    }
  };

  useEffect(() => {
    fetchQuickStats();
  }, [isLoggedIn]);

  useEffect(() => {
    if (activeTab === "responses" && localStorage.getItem("auth_token")) {
      fetchMyAnswers();
    } else {
      fetchQuestions();
    }
  }, [activeTab, isLoggedIn, languageTab, currentPage]);

  useEffect(() => {
    const handleUrlTriggerModal = async () => {
      if (urlQuestionId) {
        setIsModalOpen(true);
        setTypedAnalysis("");
        setSubmitError("");
        setIsModalLoading(true);

        const currentToken = localStorage.getItem("auth_token");
        try {
          const headers = {};
          if (currentToken) {
            headers["Authorization"] = `Bearer ${currentToken}`;
          }

          const res = await fetch(
            `${BASE_URL}/api/v1/account_questions/${urlQuestionId}`,
            { headers }
          );
          const result = await res.json();
          if (result?.data) {
            setSelectedQuestion(result.data);
          } else {
            setIsModalOpen(false);
          }
        } catch (err) {
          console.error("Failed to fetch question details:", err);
          setIsModalOpen(false);
        } finally {
          setIsModalLoading(false);
        }
      }
    };

    handleUrlTriggerModal();
  }, [urlQuestionId]);

  const handleSwitchTab = (tabName) => {
    if (!localStorage.getItem("auth_token") && tabName === "responses") return;
    setCurrentPage(1);
    navigate(`/questions?tab=${tabName}`);
  };

  const handleLanguageChange = (lang) => {
    setLanguageTab(lang);
    setCurrentPage(1);
  };

  const handleRespondButtonClick = (e, q) => {
    e.stopPropagation();
    if (q.is_answered) {
      showToast(
        "This question has already been answered. Please answer another question."
      );
      return;
    }
    handleOpenModal(e, q);
  };

  const handleOpenModal = async (e, question = null) => {
    if (e) e.stopPropagation();
    if (!question) return;

    setIsModalOpen(true);
    setTypedAnalysis("");
    setSubmitError("");
    setSelectedQuestion(question);

    navigate(`/questions?tab=${activeTab}&questionId=${question.id}`, {
      replace: true,
    });

    setIsModalLoading(true);
    const currentToken = localStorage.getItem("auth_token");
    try {
      const headers = {};
      if (currentToken) {
        headers["Authorization"] = `Bearer ${currentToken}`;
      }

      const res = await fetch(
        `${BASE_URL}/api/v1/account_questions/${question.id}`,
        { headers }
      );
      const result = await res.json();
      if (result?.data) {
        setSelectedQuestion(result.data);
      }
    } catch (err) {
      console.error("Failed to fetch question details:", err);
    } finally {
      setIsModalLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedQuestion(null);
    setSubmitError("");
    navigate(`/questions?tab=${activeTab}`, { replace: true });
  };

  const handleTextareaClick = () => {
    if (selectedQuestion?.is_answered) {
      setSubmitError(
        "This question has already been answered. Please answer another question."
      );
    }
  };

  const handleTextareaChange = (e) => {
    if (selectedQuestion?.is_answered) {
      setSubmitError(
        "This question has already been answered. Please answer another question."
      );
      return;
    }
    if (e.target.value.length <= 500) {
      setTypedAnalysis(e.target.value);
      if (e.target.value.trim().length > 0) {
        setSubmitError("");
      }
    }
  };

  const handleSubmitResponse = async () => {
    const currentToken = localStorage.getItem("auth_token");
    if (!currentToken || !selectedQuestion) return;

    if (selectedQuestion.is_answered) {
      setSubmitError(
        "This question has already been answered. Please answer another question."
      );
      return;
    }

    if (!typedAnalysis.trim()) {
      setSubmitError(
        "Response cannot be empty. Please enter your analysis to submit."
      );
      return;
    }

    const questionId = selectedQuestion.id;

    try {
      const res = await fetch(`${BASE_URL}/api/v1/account_question_answers`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${currentToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          account_question_answer: {
            account_question_id: questionId,
            content: typedAnalysis,
          },
        }),
      });

      if (res.ok) {
        setTypedAnalysis("");
        setSubmitError("");
        fetchQuestions();
        fetchMyAnswers();
        fetchQuickStats();

        try {
          const detailRes = await fetch(
            `${BASE_URL}/api/v1/account_questions/${questionId}`,
            {
              headers: { Authorization: `Bearer ${currentToken}` },
            }
          );
          const result = await detailRes.json();
          if (result?.data) {
            setSelectedQuestion(result.data);
          }
        } catch (detailErr) {
          console.error("Failed to refresh modal details:", detailErr);
          handleCloseModal();
        }
      }
    } catch (err) {
      console.error("Failed to submit response:", err);
    }
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return "";
    const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const totalPages =
    Math.ceil(
      (activeTab === "public"
        ? quickStats.question_count
        : quickStats.answer_count) / perPage
    ) || 1;

  const handlePageClick = (page) => {
    if (page === "..." || page === currentPage || isApiLoading) return;
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const getPaginationGroup = () => {
    let pages = [];
    const total = totalPages;
    const current = currentPage;

    if (total <= 7) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else {
      if (current <= 3) {
        pages = [1, 2, 3, 4, "...", total];
      } else if (current >= total - 2) {
        pages = [1, "...", total - 3, total - 2, total - 1, total];
      } else {
        pages = [1, "...", current - 1, current, current + 1, "...", total];
      }
    }
    return pages;
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const renderFeedItem = (q, idx) => {
    const isAnswered = q.is_answered || q.answers_count > 0;

    return (
      <div
        className="feed-item"
        key={q.id || idx}
        onClick={(e) => handleOpenModal(e, q)}
      >
        <div className="mobile-top-meta">
          <div className="mobile-user-info">
            <span className="user-name">{q.asked_by || "User"}</span>
            <span className="meta-divider">·</span>
            <span>{timeAgo(q.created_at)}</span>
          </div>
        </div>

        <div className="feed-table-layout">
          <div className="feed-main-cell">
            <div className="feed-main">
              <div className="feed-top-row">
                <span className="stock-badge">{q.stock_name || "STOCK"}</span>
                <span className="qty-price">
                  {q.quantity ? `Qty: ${q.quantity}` : ""}
                  {q.entry_price ? ` | Entry: ₹${q.entry_price}` : ""}
                </span>
              </div>
              <div className="feed-title">{q.content}</div>

              <div className="feed-bottom-row desktop-only">
                <span className="user-name">{q.asked_by || "User"}</span>
                <span className="meta-divider">·</span>
                <span>{timeAgo(q.created_at)}</span>
                <span className="meta-divider">·</span>
                <span className="answer-count">
                  {isAnswered ? (
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                        color: "#28a745",
                        fontWeight: "500",
                      }}
                    >
                      <svg
                        viewBox="0 0 16 16"
                        fill="currentColor"
                        style={{ width: "14px", height: "14px" }}
                      >
                        <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z" />
                      </svg>
                      Answered
                    </span>
                  ) : (
                    "⏳ No answers yet"
                  )}
                </span>
                {isLoggedIn && currentUserType === "expert" && (
                  <div className="mobile-action-container">
                    <button
                      className="action-btn primary mobile-respond-btn res-button"
                      style={
                        q.is_answered
                          ? { opacity: 0.5, cursor: "not-allowed" }
                          : {}
                      }
                      onClick={(e) => handleRespondButtonClick(e, q)}
                    >
                      Respond
                    </button>
                  </div>
                )}
              </div>

              <div className="feed-bottom-row mobile-only">
                <span className="answer-count mobile">
                  {isAnswered ? (
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                        color: "#28a745",
                        fontWeight: "500",
                      }}
                    >
                      <svg
                        viewBox="0 0 16 16"
                        fill="currentColor"
                        style={{ width: "14px", height: "14px" }}
                      >
                        <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z" />
                      </svg>
                      Answered
                    </span>
                  ) : (
                    "⏳ No answers yet"
                  )}
                </span>
              </div>
            </div>
          </div>
          <div className="feed-action-cell">
            <div className="feed-actions-right">
              <div>
                <span className="answer-count web">
                  {isAnswered ? (
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                        color: "#28a745",
                        fontWeight: "500",
                      }}
                    >
                      <svg
                        viewBox="0 0 16 16"
                        fill="currentColor"
                        style={{ width: "14px", height: "14px" }}
                      >
                        <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z" />
                      </svg>
                      Answered
                    </span>
                  ) : (
                    "⏳ No answers yet"
                  )}
                </span>
              </div>
              {isLoggedIn && currentUserType === "expert" ? (
                <>
                  <button
                    className="action-btn primary res-button"
                    style={
                      q.is_answered
                        ? { opacity: 0.5, cursor: "not-allowed" }
                        : {}
                    }
                    onClick={(e) => handleRespondButtonClick(e, q)}
                  >
                    Respond
                  </button>
                  <button
                    className="action-btn"
                    onClick={(e) => handleOpenModal(e, q)}
                  >
                    View Answers
                  </button>
                </>
              ) : (
                <button
                  className="action-btn"
                  onClick={(e) => handleOpenModal(e, q)}
                >
                  View Responses
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "var(--light)",
        position: "relative",
      }}
    >
      {/* Toast Notification */}
      {toastMessage && (
        <div
          style={{
            position: "fixed",
            bottom: "24px",
            right: "24px",
            backgroundColor: "#212529",
            color: "#ffffff",
            padding: "12px 20px",
            borderRadius: "8px",
            fontSize: "14px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            zIndex: 99999,
            display: "flex",
            alignItems: "center",
            gap: "8px",
            animation: "fadeIn 0.2s ease-in-out",
          }}
        >
          <span>⚠️ {toastMessage}</span>
        </div>
      )}

      <div className="header">
        <div className="header-wrapper">
          <div className="header-flex-replacement">
            <div className="header-left-cell">
              <div className="logo">ET Now Expert Q&amp;A</div>
            </div>
            <div className="header-right-cell"></div>
          </div>
        </div>
      </div>

      <div className="tabs-container">
        <div className="tabs">
          <div
            className={`tab ${activeTab === "public" ? "active" : ""}`}
            onClick={() => handleSwitchTab("public")}
          >
            Public Queries
          </div>
          {isLoggedIn && currentUserType === "expert" && (
            <div
              className={`tab ${activeTab === "responses" ? "active" : ""}`}
              onClick={() => handleSwitchTab("responses")}
              style={{ display: "inline-block" }}
            >
              My Responses
            </div>
          )}
        </div>
      </div>

      {activeTab === "public" && (
        <div
          className="tabs-container"
          style={{
            borderTop: "none",
            background: "#fcfcfc",
            paddingTop: "5px",
          }}
        >
          <div className="tabs" style={{ gap: "15px" }}>
            <div
              className={`tab ${languageTab === "english" ? "active" : ""}`}
              onClick={() => handleLanguageChange("english")}
              style={{
                fontSize: "14px",
                padding: "8px 16px",
                cursor: "pointer",
              }}
            >
              English
            </div>

            <div
              className={`tab ${languageTab === "hindi" ? "active" : ""}`}
              onClick={() => handleLanguageChange("hindi")}
              style={{
                fontSize: "14px",
                padding: "8px 16px",
                cursor: "pointer",
              }}
            >
              Hindi
            </div>
          </div>
        </div>
      )}

      <div className="main-wrapper" style={{ marginTop: "0px" }}>
        <div className="layout-table">
          <div className="layout-main-column">
            {isApiLoading ? (
              <div
                style={{ padding: "20px", textAlign: "center", color: "#666" }}
              >
                Loading...
              </div>
            ) : (
              <>
                {activeTab === "public" &&
                  (questions.length === 0 ? (
                    <div
                      style={{
                        padding: "20px",
                        textAlign: "center",
                        color: "#666",
                      }}
                    >
                      No queries found.
                    </div>
                  ) : (
                    <div className="feed">
                      {questions.map((q, idx) => renderFeedItem(q, idx))}
                    </div>
                  ))}

                {isLoggedIn &&
                  currentUserType === "expert" &&
                  activeTab === "responses" &&
                  (myAnswers.length === 0 ? (
                    <div
                      style={{
                        padding: "20px",
                        textAlign: "center",
                        color: "#666",
                      }}
                    >
                      No responses given yet.
                    </div>
                  ) : (
                    <div id="responses-feed" className="feed">
                      {myAnswers.map((a, idx) => {
                        const qDetails = a.question_details || {};
                        const otherExpertsCount =
                          (qDetails.answers_count || 1) - 1;

                        return (
                          <div
                            className="feed-item expert-response-card"
                            key={a.id || idx}
                          >
                            <div className="feed-main">
                              <div
                                className="feed-top-row"
                                style={{ marginBottom: "8px" }}
                              >
                                <span className="stock-badge">
                                  {qDetails.stock_name || "STOCK"}
                                </span>
                                <span className="qty-price">
                                  {qDetails.quantity
                                    ? `Qty: ${qDetails.quantity}`
                                    : ""}
                                  {qDetails.entry_price
                                    ? ` | Entry: ₹${qDetails.entry_price.toLocaleString()}`
                                    : ""}
                                </span>
                              </div>

                              <div
                                className="feed-title"
                                style={{
                                  fontSize: "16px",
                                  fontWeight: "600",
                                  marginBottom: "8px",
                                  color: "#111",
                                }}
                              >
                                {qDetails.content}
                              </div>

                              <div
                                className="feed-bottom-row"
                                style={{
                                  color: "#666",
                                  fontSize: "13px",
                                  marginBottom: "12px",
                                }}
                              >
                                <span>
                                  Asked by{" "}
                                  <strong style={{ color: "#333" }}>
                                    {qDetails.asked_by || "User"}
                                  </strong>
                                </span>
                                <span
                                  className="meta-divider"
                                  style={{ margin: "0 8px" }}
                                >
                                  ·
                                </span>
                                <span>{timeAgo(a.created_at)}</span>
                              </div>

                              <div
                                className="response-highlight-box"
                                style={{
                                  borderLeft: "3px solid #dc3545",
                                  paddingLeft: "12px",
                                  margin: "12px 0",
                                  fontSize: "14px",
                                  lineHeight: "1.5",
                                  textAlign: "left",
                                }}
                              >
                                <span
                                  style={{
                                    color: "#dc3545",
                                    fontWeight: "600",
                                  }}
                                >
                                  ✓ Your response:
                                </span>{" "}
                                <span style={{ color: "#222" }}>
                                  {a.content}
                                </span>
                              </div>

                              <div
                                className="other-experts-note"
                                style={{
                                  color: "#777",
                                  fontSize: "13px",
                                  marginTop: "8px",
                                  textAlign: "left",
                                }}
                              >
                                {otherExpertsCount > 0
                                  ? `Also answered by ${otherExpertsCount} other expert${
                                      otherExpertsCount > 1 ? "s" : ""
                                    }`
                                  : "You are the only expert who answered this query"}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}

                {((activeTab === "public" && questions.length > 0) ||
                  (activeTab === "responses" && myAnswers.length > 0)) && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: "6px",
                      padding: "24px 0 20px",
                      width: "100%",
                      flexWrap: "wrap",
                    }}
                  >
                    <button
                      onClick={handlePrevPage}
                      disabled={currentPage === 1 || isApiLoading}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "6px 12px",
                        fontSize: "13px",
                        fontWeight: "500",
                        color: currentPage === 1 ? "#a0a0a0" : "#333",
                        backgroundColor: "#ffffff",
                        border: "1px solid #dcdfe4",
                        borderRadius: "6px",
                        cursor:
                          currentPage === 1 || isApiLoading
                            ? "not-allowed"
                            : "pointer",
                        opacity: currentPage === 1 ? 0.5 : 1,
                        transition: "all 0.2s ease",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
                        outline: "none",
                      }}
                    >
                      ← Prev
                    </button>

                    {getPaginationGroup().map((page, index) => {
                      const isCurrent = page === currentPage;
                      const isDots = page === "...";

                      return (
                        <button
                          key={index}
                          onClick={() => handlePageClick(page)}
                          disabled={isDots || isApiLoading}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minWidth: "34px",
                            height: "34px",
                            padding: "0 6px",
                            fontSize: "13px",
                            fontWeight: isCurrent ? "600" : "500",
                            color: isCurrent
                              ? "#ffffff"
                              : isDots
                              ? "#888"
                              : "#333",
                            backgroundColor: isCurrent
                              ? "#dc3545"
                              : isDots
                              ? "transparent"
                              : "#ffffff",
                            border: isDots
                              ? "none"
                              : isCurrent
                              ? "1px solid #dc3545"
                              : "1px solid #dcdfe4",
                            borderRadius: "6px",
                            cursor:
                              isDots || isCurrent || isApiLoading
                                ? "default"
                                : "pointer",
                            boxShadow: isCurrent
                              ? "0 2px 4px rgba(220,53,69,0.25)"
                              : isDots
                              ? "none"
                              : "0 1px 2px rgba(0,0,0,0.03)",
                            transition: "all 0.15s ease",
                            outline: "none",
                          }}
                        >
                          {page}
                        </button>
                      );
                    })}

                    <button
                      onClick={handleNextPage}
                      disabled={currentPage >= totalPages || isApiLoading}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "6px 12px",
                        fontSize: "13px",
                        fontWeight: "500",
                        color: currentPage >= totalPages ? "#a0a0a0" : "#333",
                        backgroundColor: "#ffffff",
                        border: "1px solid #dcdfe4",
                        borderRadius: "6px",
                        cursor:
                          currentPage >= totalPages || isApiLoading
                            ? "not-allowed"
                            : "pointer",
                        opacity: currentPage >= totalPages ? 0.5 : 1,
                        transition: "all 0.2s ease",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
                        outline: "none",
                      }}
                    >
                      Next →
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="layout-sidebar-column">
            <div className="sidebar">
              <div className="sidebar-card">
                <div className="sidebar-title">Quick Stats</div>
                <div className="stat-item">
                  <span>Total Queries</span>
                  <span className="stat-value">
                    {quickStats.question_count.toLocaleString()}
                  </span>
                </div>
                <div className="stat-item">
                  <span>Expert Responses</span>
                  <span className="stat-value">
                    {quickStats.answer_count.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* WhatsApp Button */}
      {!(isLoggedIn && currentUserType === "expert") && (
        <a
          href={
            languageTab === "hindi"
              ? "https://wa.link/d0rnw8"
              : "https://wa.link/5s708h"
          }
          target="_blank"
          rel="noopener noreferrer"
          className="whatsapp-sticky-btn"
        >
          <div className="whatsapp-btn-content">
            <svg
              className="whatsapp-icon"
              viewBox="0 0 32 32"
              fill="currentColor"
            >
              <path d="M16.001 3C9.373 3 4 8.373 4 15c0 2.641.86 5.082 2.313 7.063L4 29l7.125-2.25A11.93 11.93 0 0 0 16 27c6.627 0 12-5.373 12-12S22.628 3 16.001 3zm0 21.75c-1.98 0-3.83-.578-5.375-1.57l-.385-.23-4.228 1.335 1.38-4.118-.25-.4A9.7 9.7 0 0 1 6.25 15c0-5.385 4.366-9.75 9.75-9.75 5.385 0 9.75 4.365 9.75 9.75s-4.365 9.75-9.75 9.75zm5.387-7.238c-.295-.148-1.743-.86-2.012-.958-.27-.099-.466-.148-.663.148-.198.295-.765.958-.938 1.156-.172.197-.345.222-.64.074-.295-.148-1.244-.458-2.37-1.462-.876-.78-1.467-1.742-1.64-2.037-.172-.296-.018-.456.13-.604.134-.133.296-.345.444-.517.148-.172.197-.296.295-.493.099-.198.05-.37-.024-.517-.074-.148-.663-1.6-.908-2.192-.24-.576-.485-.498-.663-.507l-.566-.01c-.197 0-.517.074-.787.37-.27.295-1.032 1.007-1.032 2.457 0 1.45 1.057 2.85 1.204 3.047.148.197 2.08 3.177 5.04 4.455.705.304 1.255.485 1.684.62.707.225 1.35.193 1.857.117.566-.084 1.743-.712 1.99-1.4.246-.688.246-1.278.172-1.4-.074-.123-.27-.197-.566-.345z" />
            </svg>
            <div className="whatsapp-text-container">
              <span className="whatsapp-title">Chat on WhatsApp</span>
              <span className="whatsapp-subtitle">
                Share your query with experts
              </span>
            </div>
          </div>
        </a>
      )}

      {/* Modal Section */}
      <div id="modal" className={`modal-overlay ${isModalOpen ? "show" : ""}`}>
        <div className="modal">
          <div className="modal-header">
            <div className="modal-title">Query Details</div>
            <button className="modal-close" onClick={handleCloseModal}>
              ✕
            </button>
          </div>
          <div className="modal-body">
            {isModalLoading ? (
              <div
                style={{ padding: "40px", textAlign: "center", color: "#666" }}
              >
                Fetching question details...
              </div>
            ) : (
              <>
                <div className="query-details">
                  <div className="detail-row">
                    <span className="detail-label">Stock</span>
                    <span className="detail-value">
                      {selectedQuestion?.stock_name || "STOCK"}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Quantity</span>
                    <span className="detail-value">
                      {selectedQuestion?.quantity || "0"}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Entry Price</span>
                    <span className="detail-value">
                      ₹{selectedQuestion?.entry_price || "0"}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Posted</span>
                    <span className="detail-value">
                      {selectedQuestion
                        ? timeAgo(selectedQuestion.created_at)
                        : ""}
                    </span>
                  </div>
                </div>
                <div className="modal-section">
                  <div className="section-title">Question</div>
                  <div className="question-text">
                    {selectedQuestion?.content}
                  </div>
                </div>
                <div className="modal-section">
                  <div className="section-title">
                    Expert Responses ({selectedQuestion?.answers_count || 0})
                  </div>
                  <div className="responses-list">
                    {selectedQuestion?.answers &&
                    selectedQuestion.answers.length > 0 ? (
                      selectedQuestion.answers.map((ans, idx) => (
                        <div className="response-item" key={ans.id || idx}>
                          <div className="response-header">
                            <span>
                              <span className="expert-name">
                                {ans.expert_name || "Expert"}
                              </span>
                              <span className="expert-label">EXPERT</span>
                            </span>
                            <span className="response-time">
                              {timeAgo(ans.created_at)}
                            </span>
                          </div>
                          <div className="response-text">{ans.content}</div>
                        </div>
                      ))
                    ) : (
                      <div
                        style={{
                          padding: "10px 0",
                          color: "#888",
                          fontSize: "14px",
                        }}
                      >
                        ⏳ No answers submitted yet.
                      </div>
                    )}
                  </div>
                </div>
                {isLoggedIn && currentUserType === "expert" && (
                  <div id="response-form" className="modal-section">
                    <div className="section-title">Add Your Response</div>
                    <div className="form-group">
                      <label className="form-label">
                        Your Expert Analysis (max 500 characters)
                      </label>
                      <textarea
                        className="form-textarea"
                        placeholder="Share your analysis..."
                        value={typedAnalysis}
                        onChange={handleTextareaChange}
                        onClick={handleTextareaClick}
                        onFocus={handleTextareaClick}
                        readOnly={selectedQuestion?.is_answered}
                        style={
                          selectedQuestion?.is_answered
                            ? {
                                opacity: 0.5,
                                cursor: "not-allowed",
                                backgroundColor: "#f8f9fa",
                              }
                            : {}
                        }
                      ></textarea>
                      {submitError && (
                        <div
                          className="validation-error-msg"
                          style={{
                            color: "#dc3545",
                            fontSize: "13px",
                            marginTop: "6px",
                            fontWeight: "500",
                          }}
                        >
                          {submitError}
                        </div>
                      )}
                      <div className="char-count">
                        {typedAnalysis.length} / 500
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          <div className="modal-footer">
            {isLoggedIn && currentUserType === "expert" ? (
              <>
                <button className="btn" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button
                  className="btn primary"
                  onClick={handleSubmitResponse}
                  disabled={selectedQuestion?.is_answered}
                  style={
                    selectedQuestion?.is_answered
                      ? {
                          opacity: 0.5,
                          cursor: "not-allowed",
                          pointerEvents: "none",
                        }
                      : {}
                  }
                >
                  Submit Response
                </button>
              </>
            ) : (
              <button className="btn" onClick={handleCloseModal}>
                Close
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpertQA;